import React, { Suspense } from 'react';
import { MediaRef, MediaRefFragment, useCreateUploadUrlMutation } from '$/GraphQL/schema/schema';
import useFormImageUpload from '$/hooks/useFormImageUpload';
import { css } from '@emotion/react';
import { LocalAsset, PageMode } from '$/components/ui/Input/PageBuilder.types';
import tw from 'twin.macro';
import stripTypenames from '$/utils/stripTypenames';
import { isValidURL } from '$/utils/isUrl';
import { InteractionBarInfo } from '../ImageOrVideoModal/ImageOrVideoModal';
import { isBase64 } from '$/utils/string';

const ImageOrVideoModal = React.lazy(() => import('../ImageOrVideoModal/ImageOrVideoModal'));

export const isValidImage = (file: File): File | undefined =>
  ['image/jpg', 'image/png', 'image/jpeg'].indexOf(file.type) !== -1 ? file : undefined;
export interface WithUploadChildrenProps {
  loading: boolean;
  value: MediaRefFragment | null | undefined;
  error: string | null;
  onRemove: () => void;
  onChange?: (url: MediaRef | null) => void;
}

export interface WithUploadProps {
  className?: string;
  name: string;
  asset: LocalAsset;
  allowThirdPartyURLs?: boolean;
  interactionBar?: InteractionBarInfo;
  avatar?: boolean;
  disabled?: boolean;
  value: MediaRefFragment | null | undefined;
  allowVideo?: boolean;
  mode?: PageMode;
  onChange?: (val: MediaRef | null | undefined) => void;
  children: (props: WithUploadChildrenProps) => React.ReactNode;
}

export interface WithUploadImageRef {
  toggleModal: (isOpen: boolean, value?: MediaRef) => void;
}

const DisableSubmitWhenUploading: React.FC<{
  fieldName: string;
  isUploading: boolean;
  skip: boolean;
}> = React.memo(({ fieldName, isUploading, skip }) => {
  // disable submit button when uploading
  useFormImageUpload({
    fieldName,
    isUploading,
    skip,
  });
  return null;
});
DisableSubmitWhenUploading.displayName = 'DisableSubmitWhenUploading';

export const WithUploadImage = React.forwardRef<WithUploadImageRef, WithUploadProps>(
  (
    {
      value,
      name,
      asset: localAsset,
      disabled,
      onChange,
      className,
      children,
      allowVideo,
      mode = PageMode.edit,
      avatar,
      allowThirdPartyURLs,
      interactionBar,
    },
    ref
  ) => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [createUploadUrlMutation] = useCreateUploadUrlMutation();
    const [showModal, setShowModal] = React.useState(false);
    const onClose = React.useCallback(() => {
      setShowModal(false);
    }, []);

    const mediaValue: MediaRef | undefined = stripTypenames(value) ?? undefined;

    React.useImperativeHandle(ref, () => ({
      toggleModal: (showModal): void => {
        setShowModal(showModal);
      },
    }));

    const onChangeMedia = React.useCallback(
      (value: MediaRefFragment | null): void => {
        if (!onChange) return;

        if (!value) {
          onChange(null);
          return;
        }

        // Remove remnant from MediaPreview type
        delete (value as any).type;
        onChange(value);
      },
      [onChange]
    );

    const onRemove = React.useCallback(() => {
      onChangeMedia?.(null);
    }, [onChangeMedia]);

    const handleUpload = React.useCallback(
      async (file: File, newValue: MediaRefFragment): Promise<void> => {
        try {
          setLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { fit, position, hideRecommended, ...asset } = localAsset;

          const response = await createUploadUrlMutation({
            variables: {
              data: {
                ...asset,
                image: {
                  width: newValue.width!,
                  height: newValue.height!,
                  type: file.type,
                },
              },
            },
          });

          if (!response.data?.createAssetUploadUrl?.uploadUrl) {
            setLoading(false);
            setError('Unable to upload image');
            return;
          }
          setError(null);
          const { url, uploadUrl } = response.data!.createAssetUploadUrl!;

          await fetch(uploadUrl!, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          const mediaRef: MediaRef | undefined = {
            ...newValue,
            url: isBase64(newValue.url) ? url : isValidURL(newValue.url) ? newValue.url : url,
            imageUrl: url, // IMGIX with default query params
          };

          if (!mediaRef.width) {
            const img = new Image();
            img.onload = (): void => {
              if (mediaRef) {
                // Save actual image dimensions
                mediaRef.width = img.width;
                mediaRef.height = img.height;
              }
              onChangeMedia?.(mediaRef);
              setLoading(false);
            };
            img.onerror = (): void => {
              setError('Unable to load image');
              setLoading(false);
            };
            img.src = mediaRef.url;
          } else {
            onChangeMedia?.(mediaRef);
            setLoading(false);
          }
        } catch (e) {
          console.error(e);
          setLoading(false);
          setError('Unable to upload image');
        }
      },
      [createUploadUrlMutation, localAsset, onChangeMedia]
    );

    return (
      <>
        <DisableSubmitWhenUploading
          fieldName={name}
          isUploading={loading}
          skip={Boolean(disabled)}
        />
        <div
          className={`flex ${className}`}
          onClick={(e): void => {
            if (mode === PageMode.preview) return;
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
          }}
          css={[
            mode === PageMode.edit
              ? css`
                  .image-show {
                    ${tw`relative z-1`}
                    &::after {
                      ${tw`absolute top-0 left-0 text-white justify-center w-full h-full items-center hidden cursor-pointer`}
                      background: rgba(0, 0, 0, 0.3);
                      backdrop-filter: blur(1px);
                      z-index: 10;
                      content: '';
                      border-radius: ${avatar ? '100' : '0'}%;
                    }
                    &::before {
                      ${tw`absolute top-0 left-0 bottom-0 right-0 text-white m-auto hidden cursor-pointer`}
                      content: '${value?.url ? 'Edit' : 'Upload'}';
                      font-size: 0.8rem;
                      padding: 0.2rem 0.8rem;
                      background: rgba(0, 0, 0, 0.5);
                      backdrop-filter: blur(1px);
                      z-index: 11;
                      max-width: 4rem;
                      min-width: 2rem;
                      height: max-content;
                      border-radius: 2rem;
                    }
                    &:hover {
                      &::before,
                      &::after {
                        ${tw`flex items-center justify-center`}
                      }
                    }
                    &.larger {
                      &::after {
                        border-radius: 1rem;
                      }
                      &::before {
                        font-size: 1.3rem !important;
                        padding: 0.5rem 2.5rem;
                      }
                    }
                  }
                `
              : '',
          ]}
        >
          {children({ loading, value: mediaValue, onChange, error, onRemove })}
        </div>
        {!disabled && showModal && (
          <Suspense>
            <ImageOrVideoModal
              error={error}
              allowThirdPartyURLs={allowThirdPartyURLs}
              interactionBar={interactionBar}
              onClose={onClose}
              asset={localAsset}
              setError={setError}
              allowVideo={allowVideo}
              value={value}
              onChange={onChangeMedia}
              handleUpload={handleUpload}
              setLoading={setLoading}
            />
          </Suspense>
        )}
      </>
    );
  }
);
WithUploadImage.displayName = 'WithUploadImage';
